import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tooltip,
  Checkbox,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { renderCompactLink } from '../utils/formatters';
import { Domain } from '../types/outreach';
import CompactFilterPanel from '../components/common/CompactFilterPanel';
import { FilterCriteria } from '../types/company';

// Mock data functions removed - not used


const TYPE_OPTIONS = [
  'THC',
  'CBD',
  'INV',
  'MED',
  'THC, CBD',
  'THC, INV',
  'CBD, INV',
  'THC, MED',
  'CBD, MED',
  'INV, MED',
  'THC, CBD, INV',
  'THC, CBD, MED',
  'THC, INV, MED',
  'CBD, INV, MED',
  'THC, CBD, INV, MED',
];

// Tag to Type mapping based on user requirements
const TAG_TO_TYPE_MAP: Record<string, string> = {
  'Association': 'THC',
  'Banking': 'INV',
  'Cannabis Industry': 'THC',
  'Cannabis Professional': 'THC',
  'Cannabis School': 'THC',
  'CBD Affiliate Network': 'CBD',
  'Cbd Association': 'CBD',
  'Cbd Broker': 'CBD',
  'CBD Chiropractor': 'CBD',
  'Cbd Consultant': 'CBD',
  'Cbd Cosmetics': 'CBD',
  'CBD Doctor': 'CBD',
  'Cbd Edibles': 'CBD',
  'CBD Events': 'CBD',
  'Cbd Extractor': 'CBD',
  'CBD Franchise': 'CBD',
  'CBD Industry': 'CBD',
  'CBD Manufacturer': 'CBD',
  'Cbd Marketing': 'CBD',
  'Massage Therapist': 'CBD',
  'Cbd Media': 'CBD',
  'CBD Online Store': 'CBD',
  'Cbd Pets': 'CBD',
  'CBD Product': 'CBD',
  'CBD Professional': 'CBD',
  'CBD Shop': 'CBD',
  'Cbd Sport': 'CBD',
  'Cbd Store': 'CBD',
  'Cbd Training': 'CBD',
  'Cbd Vape': 'CBD',
  'CBD Wellness': 'CBD',
  'Cbd Wholesaler': 'CBD',
  'Chiropractor': 'CBD',
  'Construction': 'THC',
  'Consumer Accessories': 'THC',
  'Delivery': 'THC',
  'Dispensary': 'THC',
  'Distributor': 'THC',
  'Doctors': 'MED',
  'Equipment Installation': 'THC',
  'Events': 'THC',
  'Extractor': 'THC',
  'Growers': 'THC',
  'Growers support': 'THC',
  'Hospital & Healthcare': 'MED',
  'Influencer': 'THC',
  'Instagram consumer': 'THC',
  'Insurance': 'INV',
  'Investors': 'INV',
  'Lab': 'THC',
  'Lawyers': 'THC, INV',
  'Manufacturer Support': 'YHC', // Note: YHC might be a typo, keeping as is
  'Marketing': 'THC',
  'Media': 'THC',
  'Online Cbd Shop': 'CBD',
  'Packaging': 'THC',
  'Payment processor': 'INV',
  'Pharmaceutical': 'MED',
  'Real Estate': 'THC',
  'School': 'THC',
  'Security': 'THC',
  'Seeds': 'THC',
  'Software Company': 'THC',
  'Speakers': 'THC',
  'State Government': 'THC',
  'THC Manufacturer': 'THC',
  'THCA Manufacturer': 'THC',
  'Transporter': 'THC',
  'Head Shop': 'CBD',
  'Wealth Managers': 'INV',
  'Wellness Center': 'CBD',
  'Writers': 'THC',
  'CBD solo store': 'CBD',
  'Post liker': 'THC',
  'Journalist': 'THC, CBD',
  'Accountants': 'INV',
  'Psychedelic': 'THC',
  'Fertilizer Distributor': 'THC or CBD', // Special case - will use first tag's type
  'Cannabis Lab': 'THC',
  'Hemp Lab': 'CBD',
  'Hydroponics Store': 'THC or CBD', // Special case
  'Lab support': 'THC',
  'Hemp cultivator': 'CBD',
  'Cannabis Association': 'THC',
  'Cannabis Bank': 'INV',
  'Hemp Bank': 'CBD',
  'THC Cultivator': 'THC',
  'THC Lab': 'THC',
  'Fertilizer Company': 'THC or CBD', // Special case
  'Recover Center': 'MED',
  'Social Justice': 'THC',
  'Nurses': 'CBD, MED',
  'Pharmacists': 'CBD, MED',
  'Budtender': 'THC',
  'Twitter': 'THC',
  'THC Consultant': 'THC',
  'THC Association': 'THC',
  'Consultants': 'THC, CBD', // Note: This overwrites the earlier 'Consultants': 'THC'
  'Hydroponic distributor': 'THC',
};

// Helper function to determine default type from selected tags
const getDefaultTypeFromTags = (selectedTags: { id: number; name: string }[]): string | null => {
  if (!selectedTags || selectedTags.length === 0) {
    return null;
  }

  // Collect all unique types from selected tags
  const typeSet = new Set<string>();
  let hasAmbiguousTags = false;
  
  // First pass: collect clear types (non-ambiguous)
  for (const tag of selectedTags) {
    const tagName = tag.name;
    const type = TAG_TO_TYPE_MAP[tagName];
    
    if (type) {
      // Handle special cases like "THC or CBD" - mark as ambiguous
      if (type === 'THC or CBD') {
        hasAmbiguousTags = true;
        continue;
      }
      
      // Handle multiple types (e.g., "THC, INV")
      if (type.includes(',')) {
        const types = type.split(',').map(t => t.trim());
        types.forEach(t => typeSet.add(t));
      } else {
        typeSet.add(type);
      }
    }
  }
  
  // If we have ambiguous tags and no clear types, default to THC
  if (typeSet.size === 0 && hasAmbiguousTags) {
    typeSet.add('THC');
  }
  
  if (typeSet.size === 0) {
    return null;
  }
  
  // Convert set to array and sort according to TYPE_OPTIONS order (THC, CBD, INV, MED)
  const typeOrder = ['THC', 'CBD', 'INV', 'MED'];
  const types = Array.from(typeSet).sort((a, b) => {
    const indexA = typeOrder.indexOf(a);
    const indexB = typeOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
  
  // Return the appropriate type string
  if (types.length === 1) {
    return types[0];
  } else if (types.length === 2) {
    return `${types[0]}, ${types[1]}`;
  } else if (types.length === 3) {
    return `${types[0]}, ${types[1]}, ${types[2]}`;
  } else {
    return `${types[0]}, ${types[1]}, ${types[2]}, ${types[3]}`;
  }
};

const OutreachProcessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  const [newDomains, setNewDomains] = useState<Domain[]>([]);
  const [oldDomains, setOldDomains] = useState<Domain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [newDomainsDisplayCount, setNewDomainsDisplayCount] = useState(50);
  const [oldDomainsDisplayCount, setOldDomainsDisplayCount] = useState(50);
  const [isLoadingMoreNew, setIsLoadingMoreNew] = useState(false);
  const [isLoadingMoreOld, setIsLoadingMoreOld] = useState(false);
  const scrollPositionRef = useRef<number>(0);
  
  // State for selection - Old Domains
  const [selectedOldDomains, setSelectedOldDomains] = useState<Set<number>>(new Set());
  const [selectAllOld, setSelectAllOld] = useState(false);
  
  // State for sorting - Old Domains
  const [oldDomainsSortField, setOldDomainsSortField] = useState<string | null>(null);
  const [oldDomainsSortDirection, setOldDomainsSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // State for filtering - Old Domains
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteria[]>([]);
  const [visibleColumns, setVisibleColumns] = useState(
    [
      { id: 'website', label: 'Website', visible: true },
      { id: 'name', label: 'Company Name', visible: true },
      { id: 'clean_name', label: 'Clean Name', visible: true },
      { id: 'note', label: 'Notes', visible: true },
      { id: 'description', label: 'Description', visible: true },
      { id: 'unsubscribe', label: 'Un', visible: true },
      { id: 'in_mailer', label: 'In', visible: true },
      { id: 'state_name', label: 'State', visible: true },
      { id: 'location', label: 'Location', visible: true },
      { id: 'country_name', label: 'Country', visible: true },
      { id: 'company_address', label: 'Company Address', visible: true },
      { id: 'snov_id', label: 'Snov ID', visible: true },
      { id: 'industry_tag_name', label: 'Industry Tag', visible: true },
      { id: 'industry_tag_two_name', label: 'Industry Tag Two', visible: true },
      { id: 'industry_tag_three_name', label: 'Industry Tag Three', visible: true },
      { id: 'industry_tag_four_name', label: 'Industry Tag Four', visible: true },
      { id: 'deliverable', label: 'Deliverable', visible: true },
      { id: 'risky', label: 'Risky', visible: true },
      { id: 'undeliverable', label: 'Undeliverable', visible: true },
      { id: 'bounce', label: 'Bounce', visible: true },
      { id: 'Linkedin', label: 'LI', visible: true },
      { id: 'employees_onlinkedin', label: 'Employees on LinkedIn', visible: true },
      { id: 'linkedin_id', label: 'LinkedIn ID', visible: true },
      { id: 'twitter', label: 'TW', visible: true },
      { id: 'Facebook', label: 'FB', visible: true },
      { id: 'ig', label: 'IG', visible: true },
      { id: 'Youtube', label: 'YT', visible: true },
      { id: 'license', label: 'License', visible: true },
      { id: 'phone', label: 'Phone', visible: true },
      { id: 'unknown', label: 'Unknown', visible: true },
      { id: 'not_verified', label: 'Not Verified', visible: true },
      { id: 'suspicious', label: 'Suspicious', visible: true },
      { id: 'type_', label: 'Type', visible: true },
      { id: 'status', label: 'Status', visible: true },
    ]
  );
  
  const [industryTags, setIndustryTags] = useState<{ id: number; name: string }[]>([]);
  
  // State for confirmation dialog
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // State for session saving
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // State to track which Autocomplete dropdowns are open (domain ID -> boolean)
  const [openDropdowns, setOpenDropdowns] = useState<Map<number, boolean>>(new Map());

  const loadStagingData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await ApiService.getStagingData(sessionId!);
      
      // Check if data has the expected structure
      if (!data || (!data.newDomains && !data.oldDomains)) {
        throw new Error('Invalid response format from server');
      }
      
      // Set all domains without filtering (treat industry_tag = 299 like any other tag)
      const newDoms = data.newDomains || [];
      const oldDoms = data.oldDomains || [];
      setNewDomains(newDoms);
      setOldDomains(oldDoms);
      
      // Initialize selected old domains from the database state
      const initiallySelected = new Set<number>(
        oldDoms.filter((d: any) => d.is_selected === 1 || d.is_selected === true).map((d: any) => d.id)
      );
      setSelectedOldDomains(initiallySelected);
      setSelectAllOld(initiallySelected.size > 0 && initiallySelected.size === oldDoms.length);
      
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading staging data:', err);
      let errorMessage = 'Failed to load staging data';
      
      if (err instanceof Error) {
        // Check if it's a network/server error
        if (err.message.includes('Failed to fetch') || 
            err.message.includes('NetworkError') ||
            err.message.includes('Network request failed') ||
            err.name === 'TypeError') {
          errorMessage = 'Server not running. Please start the server';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [sessionId]);

  // Load staging data on mount
  useEffect(() => {
    if (!sessionId) {
      console.error('No session ID in URL. Expected format: /outreach/process?session=SESSION_ID');
      setError('No session ID provided. Please start from the upload page.');
      setIsLoading(false);
      return;
    }
    
    loadStagingData();
  }, [sessionId, loadStagingData]);

  useEffect(() => {
    const fetchIndustryTags = async () => {
      try {
        const response = await ApiService.getOutreachIndustryTags();
        const tagList = response && Array.isArray(response.industryTags) ? response.industryTags : [];
        setIndustryTags(tagList);
      } catch (err) {
        console.error('Error fetching industry tags:', err);
      }
    };

    fetchIndustryTags();
  }, []);

  // Load more handler for New Domains
  const handleLoadMoreNew = () => {
    if (newDomainsDisplayCount < newDomains.length && !isLoadingMoreNew) {
      setIsLoadingMoreNew(true);
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      setTimeout(() => {
        setNewDomainsDisplayCount(prev => Math.min(prev + 50, newDomains.length));
        setIsLoadingMoreNew(false);
        
        // Restore scroll position
        if (scrollPositionRef.current > 0) {
          setTimeout(() => {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0;
          }, 50);
        }
      }, 300);
    }
  };

  // Load more handler for Old Domains
  const handleLoadMoreOld = () => {
    const maxCount = filteredOldDomains.length;
    if (oldDomainsDisplayCount < maxCount && !isLoadingMoreOld) {
      setIsLoadingMoreOld(true);
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      setTimeout(() => {
        setOldDomainsDisplayCount(prev => Math.min(prev + 50, maxCount));
        setIsLoadingMoreOld(false);
        
        // Restore scroll position
        if (scrollPositionRef.current > 0) {
          setTimeout(() => {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0;
          }, 50);
        }
      }, 300);
    }
  };

  // Handle industry tags change (multi-select up to 4 tags)
  const handleStatusChange = async (id: number, selectedTags: { id: number; name: string }[]) => {
    try {
      // Limit to 4 tags and extract IDs
      const tagIds = selectedTags.slice(0, 4).map(tag => tag.id);
      
      // Call new endpoint to update all 4 columns
      await ApiService.updateDomainIndustryTags(sessionId!, id, tagIds);
      
      // Map tags to columns: [tag1, tag2, tag3, tag4] -> industry_tag, industry_tag_two, industry_tag_three, industry_tag_four
      const industryTag = tagIds[0] || undefined;
      const industryTagTwo = tagIds[1] || undefined;
      const industryTagThree = tagIds[2] || undefined;
      const industryTagFour = tagIds[3] || undefined;
      
      const industryTagName = selectedTags[0]?.name || undefined;
      const industryTagTwoName = selectedTags[1]?.name || undefined;
      const industryTagThreeName = selectedTags[2]?.name || undefined;
      const industryTagFourName = selectedTags[3]?.name || undefined;
      
      // Auto-populate type based on selected tags
      const defaultType = getDefaultTypeFromTags(selectedTags);
      if (defaultType) {
        // Always auto-populate type when tags are selected (user can change it manually if needed)
        await handleTypeChange(id, defaultType);
      }
      
      // Update local state immediately without reloading (smooth update like type)
      setNewDomains(prev => prev.map(d => 
        d.id === id ? { 
          ...d, 
          industry_tag: industryTag,
          industry_tag_name: industryTagName,
          industry_tag_two: industryTagTwo,
          industry_tag_two_name: industryTagTwoName,
          industry_tag_three: industryTagThree,
          industry_tag_three_name: industryTagThreeName,
          industry_tag_four: industryTagFour,
          industry_tag_four_name: industryTagFourName
        } : d
      ));
      
      // Also update old domains if it exists there
      setOldDomains(prev => prev.map(d => 
        d.id === id ? { 
          ...d, 
          industry_tag: industryTag,
          industry_tag_name: industryTagName,
          industry_tag_two: industryTagTwo,
          industry_tag_two_name: industryTagTwoName,
          industry_tag_three: industryTagThree,
          industry_tag_three_name: industryTagThreeName,
          industry_tag_four: industryTagFour,
          industry_tag_four_name: industryTagFourName
        } : d
      ));
    } catch (err) {
      console.error('Error updating industry tags:', err);
    }
  };

  // Handle type change
  const handleTypeChange = async (id: number, type: string) => {
    try {
      await ApiService.updateDomainType(sessionId!, id, type);
      
      const typeValue = type === '' ? null : type;
      
      // Update local state immediately without reloading (record stays visible)
      setNewDomains(prev => prev.map(d => 
        d.id === id ? { ...d, type_: typeValue } : d
      ));
      
      // Also update old domains if it exists there
      setOldDomains(prev => prev.map(d => 
        d.id === id ? { ...d, type_: typeValue } : d
      ));
    } catch (err) {
      console.error('Error updating type:', err);
    }
  };

  // Handle select all - Old Domains
  const handleSelectAllOld = () => {
    if (selectAllOld) {
      setSelectedOldDomains(new Set());
    } else {
      const allIds = oldDomains.map(d => d.id);
      setSelectedOldDomains(new Set(allIds));
    }
    setSelectAllOld(!selectAllOld);
  };

  // Handle individual selection - Old Domains
  const handleSelectOldDomain = (id: number) => {
    const newSelected = new Set(selectedOldDomains);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedOldDomains(newSelected);
    setSelectAllOld(newSelected.size === oldDomains.length);
  };

  // Handle continue to next page
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      
      // Get all new domain IDs where exists_in_db = false (treat all industry_tags equally)
      const newDomainIds = newDomains
        .filter(d => !d.exists_in_db)
        .map(d => d.id);
      
      // Get selected old domain IDs
      const oldDomainIds = Array.from(selectedOldDomains);
      
      // Combine all IDs to mark as selected
      const allSelectedIds = [...newDomainIds, ...oldDomainIds];
      
      if (allSelectedIds.length === 0) {
        alert('No domains to continue with. Please select at least one domain or ensure new domains have appropriate tags.');
        setIsLoading(false);
        return;
      }
      
      // Mark domains as selected in the backend
      await ApiService.updateDomainSelection(sessionId!, allSelectedIds, true);
      
      // Navigate to domain list page with session ID
      navigate(`/outreach/domain-list?session=${sessionId}`);
    } catch (err) {
      console.error('Error continuing to next page:', err);
      setError('Failed to continue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSession = async () => {
    if (!sessionName.trim()) {
      alert('Please enter a name for this session.');
      return;
    }

    try {
      setIsSaving(true);
      
      // 1. Get all IDs to save (everything currently in the view)
      const newDomainIds = newDomains.map(d => d.id);
      const oldDomainIds = Array.from(selectedOldDomains);
      const allIds = [...newDomainIds, ...oldDomainIds];

      // 2. Mark them as selected first (so saveForLater picks them up)
      if (allIds.length > 0) {
        await ApiService.updateDomainSelection(sessionId!, allIds, true);
      }

      // 3. Save for later
      const result = await ApiService.saveForLater(sessionId!, allIds, sessionName.trim());
      
      setSaveSuccess(`Session "${sessionName}" saved with ${result.savedDomains} domains.`);
      setShowSaveDialog(false);
      
      // Optionally navigate back to home after saving
      setTimeout(() => {
        navigate('/outreach');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error saving session:', err);
      alert(`Failed to save session: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle sorting - Old Domains
  const handleSort = (field: string) => {
    if (oldDomainsSortField === field) {
      setOldDomainsSortDirection(oldDomainsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOldDomainsSortField(field);
      setOldDomainsSortDirection('asc');
    }
  };

  // Filter old domains based on applied filters
  const filteredOldDomains = React.useMemo(() => {
    if (appliedFilters.length === 0) return oldDomains;
    
    return oldDomains.filter((domain: any) => {
      let result: boolean | null = null;
      
      for (let i = 0; i < appliedFilters.length; i++) {
        const filter = appliedFilters[i];
        
        let matches = false;
        
        // Special handling for industry_tag - check all 4 columns (industry_tag_name, industry_tag_two_name, industry_tag_three_name, industry_tag_four_name)
        if (filter.field === 'industry_tag') {
          const industryTagFields = [
            domain.industry_tag_name,
            domain.industry_tag_two_name,
            domain.industry_tag_three_name,
            domain.industry_tag_four_name
          ];
          const filterValue = String(filter.value || '').toLowerCase();
          
          switch (filter.operator) {
            case 'like':
              // Check if any of the 4 industry tag names contains the filter value
              matches = industryTagFields.some(tag => 
                tag != null && String(tag).toLowerCase().includes(filterValue)
              );
              break;
            case 'not_like':
              // Check if all 4 industry tag names do NOT contain the filter value
              matches = industryTagFields.every(tag => 
                tag == null || !String(tag).toLowerCase().includes(filterValue)
              );
              break;
            case 'equals':
              // Check if any of the 4 industry tag names equals the filter value
              matches = industryTagFields.some(tag => 
                tag != null && String(tag).toLowerCase() === filterValue
              );
              break;
            case 'not_equals':
              // Check if all 4 industry tag names do NOT equal the filter value
              matches = industryTagFields.every(tag => 
                tag == null || String(tag).toLowerCase() !== filterValue
              );
              break;
            case 'is_null':
              // All 4 industry tag columns must be null/empty
              matches = industryTagFields.every(tag => 
                tag == null || tag === '' || tag === undefined
              );
              break;
            case 'is_not_null':
              // At least one of the 4 industry tag columns must be not null
              matches = industryTagFields.some(tag => 
                tag != null && tag !== '' && tag !== undefined
              );
              break;
            default:
              matches = false;
          }
        } else {
          // Regular field filtering
          const fieldName = filter.field;
          const fieldValue = domain[fieldName];
          const filterValue = filter.value;
          
          // Handle null/undefined checks first
          if (filter.operator === 'is_null') {
            matches = fieldValue == null || fieldValue === '' || fieldValue === undefined;
          } else if (filter.operator === 'is_not_null') {
            matches = fieldValue != null && fieldValue !== '' && fieldValue !== undefined;
          } else {
            // For other operators, if field is null/undefined, it doesn't match (except for is_null)
            if (fieldValue == null || fieldValue === '' || fieldValue === undefined) {
              matches = false;
            } else {
              // Numeric fields
              const numericFields = ['deliverable', 'risky', 'unknown', 'undeliverable', 'not_verified', 'in_mailer', 'unsubscribe', 'bounce'];
              const isNumericField = numericFields.includes(filter.field);
              
              if (isNumericField) {
                const numValue = Number(fieldValue);
                const numFilter = Number(filterValue);
                
                switch (filter.operator) {
                  case 'equals':
                    matches = numValue === numFilter;
                    break;
                  case 'not_equals':
                    matches = numValue !== numFilter;
                    break;
                  default:
                    matches = false;
                }
              } else {
                // String fields
                const strValue = String(fieldValue).toLowerCase();
                const strFilter = String(filterValue).toLowerCase();
                
                switch (filter.operator) {
                  case 'like':
                    matches = strValue.includes(strFilter);
                    break;
                  case 'not_like':
                    matches = !strValue.includes(strFilter);
                    break;
                  case 'equals':
                    matches = strValue === strFilter;
                    break;
                  case 'not_equals':
                    matches = strValue !== strFilter;
                    break;
                  default:
                    matches = false;
                }
              }
            }
          }
        }
        
        // Combine with previous results using logic
        if (result === null) {
          // First filter
          result = matches;
        } else {
          // Combine with previous result
          if (filter.logic === 'AND') {
            result = result && matches;
          } else {
            result = result || matches;
          }
        }
      }
      
      return result === true;
    });
  }, [oldDomains, appliedFilters]);
  
  // Sort old domains
  const sortedOldDomains = React.useMemo(() => {
    if (!oldDomainsSortField) return filteredOldDomains;
    
    return [...filteredOldDomains].sort((a: any, b: any) => {
      const aValue = a[oldDomainsSortField];
      const bValue = b[oldDomainsSortField];
      
      if (aValue < bValue) return oldDomainsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return oldDomainsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOldDomains, oldDomainsSortField, oldDomainsSortDirection]);
  
  // Filter handlers
  const handleFiltersApply = (newFilters: FilterCriteria[]) => {
    setAppliedFilters(newFilters);
    setOldDomainsDisplayCount(50); // Reset display count when filters change
  };
  
  const handleClearFilters = () => {
    setAppliedFilters([]);
    setOldDomainsDisplayCount(50); // Reset display count when filters cleared
  };
  
  const handleColumnToggle = (columnId: string, visible: boolean) => {
    setVisibleColumns(prev => prev.map(col =>
      col.id === columnId ? { ...col, visible } : col
    ));
  };

  // Helper to get minWidth for each column (for spacing optimization)
  const getColumnMinWidth = (columnId: string): number => {
    const widths: Record<string, number> = {
      'deliverable': 45, 'risky': 45, 'undeliverable': 45, 'bounce': 45,
      'description': 400, 'unsubscribe': 45, 'in_mailer': 45,
      'website': 150, 'name': 180, 'clean_name': 150, 'note': 120,
      'state_name': 80, 'location': 120, 'country_name': 100,
      'company_address': 150, 'snov_id': 100, 'industry_tag_name': 130,
      'industry_tag_two_name': 130, 'industry_tag_three_name': 130,
      'industry_tag_four_name': 130, 'Linkedin': 50, 'employees_onlinkedin': 140,
      'linkedin_id': 100, 'twitter': 50, 'Facebook': 50, 'ig': 50,
      'Youtube': 50, 'license': 100, 'phone': 100, 'unknown': 80,
      'not_verified': 100, 'suspicious': 100, 'type_': 100, 'status': 80,
      'industry_tag': 130, 'type': 100
    };
    return widths[columnId] || 120;
  };

  // Helper to check if column should be visible
  const isColumnVisible = (columnId: string): boolean => {
    // Selection checkboxes are required for Continue / Save for Later — never hide them.
    if (columnId === 'checkbox') return true;
    const col = visibleColumns.find(c => c.id === columnId);
    return col ? col.visible : false;
  };

  // Conditional cell wrapper - returns null if column should be hidden
  const Cell = ({ id, children, sx, ...props }: any) => {
    if (!isColumnVisible(id)) return null;
    return <TableCell sx={sx} {...props}>{children}</TableCell>;
  };

  // Render new domain table row with conditional columns
  const renderNewDomainRow = (domain: any) => {
    const cells = [
      { id: 'industry_tag', render: () => (
        <TableCell key="industry_tag">{domain.industry_tag_name || '-'}</TableCell>
      )},
      { id: 'type_', render: () => (
        <TableCell key="type_">{domain.type_ || '-'}</TableCell>
      )},
      { id: 'website', render: () => (
        <TableCell key="website" sx={{ fontWeight: 'medium' }}>
          {renderCompactLink(domain.website || domain.domain, 'website')}
        </TableCell>
      )},
      { id: 'name', render: () => (
        <TableCell key="name">{domain.name || domain.company_name || '-'}</TableCell>
      )},
      { id: 'clean_name', render: () => (
        <TableCell key="clean_name">{domain.clean_name || '-'}</TableCell>
      )},
      { id: 'note', render: () => (
        <TableCell key="note">
          <Tooltip title={String(domain.note || '')} arrow>
            <span>{truncateText(String(domain.note || ''), 30)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'description', render: () => (
        <TableCell key="description">
          <Tooltip title={String(domain.description || domain.company_description || '')} arrow>
            <span>{truncateText(String(domain.description || domain.company_description || ''), 80)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'unsubscribe', render: () => (
        <TableCell key="unsubscribe" align="right">{domain.unsubscribe ?? '-'}</TableCell>
      )},
      { id: 'in_mailer', render: () => (
        <TableCell key="in_mailer" align="right">{domain.in_mailer ?? '-'}</TableCell>
      )},
      { id: 'state_name', render: () => (
        <TableCell key="state_name">{domain.state_name || '-'}</TableCell>
      )},
      { id: 'location', render: () => (
        <TableCell key="location">{domain.location || '-'}</TableCell>
      )},
      { id: 'country_name', render: () => (
        <TableCell key="country_name">{domain.country_name || '-'}</TableCell>
      )},
      { id: 'company_address', render: () => (
        <TableCell key="company_address">
          <Tooltip title={String(domain.company_address || '')} arrow>
            <span>{truncateText(String(domain.company_address || ''), 30)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'snov_id', render: () => (
        <TableCell key="snov_id">{domain.snov_id || '-'}</TableCell>
      )},
      { id: 'industry_tag_name', render: () => (
        <TableCell key="industry_tag_name">{domain.industry_tag_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_two_name', render: () => (
        <TableCell key="industry_tag_two_name">{domain.industry_tag_two_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_three_name', render: () => (
        <TableCell key="industry_tag_three_name">{domain.industry_tag_three_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_four_name', render: () => (
        <TableCell key="industry_tag_four_name">{domain.industry_tag_four_name || '-'}</TableCell>
      )},
      { id: 'deliverable', render: () => (
        <TableCell key="deliverable" align="right">{domain.deliverable ?? '-'}</TableCell>
      )},
      { id: 'risky', render: () => (
        <TableCell key="risky" align="right">{domain.risky ?? '-'}</TableCell>
      )},
      { id: 'undeliverable', render: () => (
        <TableCell key="undeliverable" align="right">{domain.undeliverable ?? '-'}</TableCell>
      )},
      { id: 'bounce', render: () => (
        <TableCell key="bounce" align="right">{domain.bounce ?? '-'}</TableCell>
      )},
      { id: 'Linkedin', render: () => (
        <TableCell key="Linkedin">{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
      )},
      { id: 'employees_onlinkedin', render: () => (
        <TableCell key="employees_onlinkedin">{domain.employees_onlinkedin || '-'}</TableCell>
      )},
      { id: 'linkedin_id', render: () => (
        <TableCell key="linkedin_id">{domain.linkedin_id || '-'}</TableCell>
      )},
      { id: 'twitter', render: () => (
        <TableCell key="twitter">{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
      )},
      { id: 'Facebook', render: () => (
        <TableCell key="Facebook">{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
      )},
      { id: 'ig', render: () => (
        <TableCell key="ig">{renderCompactLink(domain.ig, 'ig')}</TableCell>
      )},
      { id: 'Youtube', render: () => (
        <TableCell key="Youtube">{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
      )},
      { id: 'license', render: () => (
        <TableCell key="license">{domain.license || '-'}</TableCell>
      )},
      { id: 'phone', render: () => (
        <TableCell key="phone">{domain.phone || '-'}</TableCell>
      )},
      { id: 'unknown', render: () => (
        <TableCell key="unknown" align="right">{domain.unknown ?? '-'}</TableCell>
      )},
      { id: 'not_verified', render: () => (
        <TableCell key="not_verified" align="right">{domain.not_verified ?? '-'}</TableCell>
      )},
      { id: 'suspicious', render: () => (
        <TableCell key="suspicious">{domain.suspicious || '-'}</TableCell>
      )},
      { id: 'status', render: () => (
        <TableCell key="status">{domain.status || '-'}</TableCell>
      )},
    ];

    return cells.filter(c => isColumnVisible(c.id)).map(c => c.render());
  };

  // Render new domains table header with conditional columns
  const renderNewDomainHeaderCells = () => {
    const headerCells = [
      { id: 'industry_tag', render: () => (
        <TableCell key="industry_tag" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag</TableCell>
      )},
      { id: 'type_', render: () => (
        <TableCell key="type_" sx={{ minWidth: 100, fontWeight: 'bold' }}>Type</TableCell>
      )},
      { id: 'website', render: () => (
        <TableCell key="website" sx={{ minWidth: 150, fontWeight: 'bold' }}>Website</TableCell>
      )},
      { id: 'name', render: () => (
        <TableCell key="name" sx={{ minWidth: 200, fontWeight: 'bold' }}>Company Name</TableCell>
      )},
      { id: 'clean_name', render: () => (
        <TableCell key="clean_name" sx={{ minWidth: 200, fontWeight: 'bold' }}>Clean Name</TableCell>
      )},
      { id: 'note', render: () => (
        <TableCell key="note" sx={{ minWidth: 200, fontWeight: 'bold' }}>Notes</TableCell>
      )},
      { id: 'description', render: () => (
        <TableCell key="description" sx={{ minWidth: 400, fontWeight: 'bold' }}>Description</TableCell>
      )},
      { id: 'unsubscribe', render: () => (
        <TableCell key="unsubscribe" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Unsubscribe" arrow>
            <span>Un</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'in_mailer', render: () => (
        <TableCell key="in_mailer" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Inmailer" arrow>
            <span>In</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'state_name', render: () => (
        <TableCell key="state_name" sx={{ minWidth: 100, fontWeight: 'bold' }}>State</TableCell>
      )},
      { id: 'location', render: () => (
        <TableCell key="location" sx={{ minWidth: 150, fontWeight: 'bold' }}>Location</TableCell>
      )},
      { id: 'country_name', render: () => (
        <TableCell key="country_name" sx={{ minWidth: 100, fontWeight: 'bold' }}>Country</TableCell>
      )},
      { id: 'company_address', render: () => (
        <TableCell key="company_address" sx={{ minWidth: 200, fontWeight: 'bold' }}>Company Address</TableCell>
      )},
      { id: 'snov_id', render: () => (
        <TableCell key="snov_id" sx={{ minWidth: 120, fontWeight: 'bold' }}>Snov ID</TableCell>
      )},
      { id: 'industry_tag_name', render: () => (
        <TableCell key="industry_tag_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag</TableCell>
      )},
      { id: 'industry_tag_two_name', render: () => (
        <TableCell key="industry_tag_two_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Two</TableCell>
      )},
      { id: 'industry_tag_three_name', render: () => (
        <TableCell key="industry_tag_three_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Three</TableCell>
      )},
      { id: 'industry_tag_four_name', render: () => (
        <TableCell key="industry_tag_four_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Four</TableCell>
      )},
      { id: 'deliverable', render: () => (
        <TableCell key="deliverable" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Deliverable" arrow>
            <span>Del</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'risky', render: () => (
        <TableCell key="risky" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Risky" arrow>
            <span>Ris</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'undeliverable', render: () => (
        <TableCell key="undeliverable" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Undeliverable" arrow>
            <span>Und</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'bounce', render: () => (
        <TableCell key="bounce" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Bounce" arrow>
            <span>Bou</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'Linkedin', render: () => (
        <TableCell key="Linkedin" sx={{ minWidth: 60, fontWeight: 'bold' }}>LI</TableCell>
      )},
      { id: 'employees_onlinkedin', render: () => (
        <TableCell key="employees_onlinkedin" sx={{ minWidth: 150, fontWeight: 'bold' }}>Employees on LinkedIn</TableCell>
      )},
      { id: 'linkedin_id', render: () => (
        <TableCell key="linkedin_id" sx={{ minWidth: 150, fontWeight: 'bold' }}>LinkedIn ID</TableCell>
      )},
      { id: 'twitter', render: () => (
        <TableCell key="twitter" sx={{ minWidth: 60, fontWeight: 'bold' }}>TW</TableCell>
      )},
      { id: 'Facebook', render: () => (
        <TableCell key="Facebook" sx={{ minWidth: 60, fontWeight: 'bold' }}>FB</TableCell>
      )},
      { id: 'ig', render: () => (
        <TableCell key="ig" sx={{ minWidth: 60, fontWeight: 'bold' }}>IG</TableCell>
      )},
      { id: 'Youtube', render: () => (
        <TableCell key="Youtube" sx={{ minWidth: 60, fontWeight: 'bold' }}>YT</TableCell>
      )},
      { id: 'license', render: () => (
        <TableCell key="license" sx={{ minWidth: 120, fontWeight: 'bold' }}>License</TableCell>
      )},
      { id: 'phone', render: () => (
        <TableCell key="phone" sx={{ minWidth: 150, fontWeight: 'bold' }}>Phone</TableCell>
      )},
      { id: 'unknown', render: () => (
        <TableCell key="unknown" align="right" sx={{ minWidth: 100, fontWeight: 'bold' }}>Unknown</TableCell>
      )},
      { id: 'not_verified', render: () => (
        <TableCell key="not_verified" align="right" sx={{ minWidth: 120, fontWeight: 'bold' }}>Not Verified</TableCell>
      )},
      { id: 'suspicious', render: () => (
        <TableCell key="suspicious" sx={{ minWidth: 120, fontWeight: 'bold' }}>Suspicious</TableCell>
      )},
      { id: 'status', render: () => (
        <TableCell key="status" sx={{ minWidth: 100, fontWeight: 'bold' }}>Status</TableCell>
      )},
    ];

    return headerCells.filter(c => isColumnVisible(c.id)).map(c => c.render());
  };

  // Render old domains table header with conditional columns
  const renderOldDomainHeaderCells = () => {
    const headerCells = [
      { id: 'checkbox', render: () => (
        <TableCell key="checkbox" sx={{ minWidth: 50, fontWeight: 'bold' }}>
          <Checkbox
            checked={selectAllOld}
            onChange={handleSelectAllOld}
            size="small"
          />
        </TableCell>
      )},
      { id: 'website', render: () => (
        <TableCell key="website" sx={{ minWidth: 150, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('website')}>
            Website
            {oldDomainsSortField === 'website' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'name', render: () => (
        <TableCell key="name" sx={{ minWidth: 200, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
            Company Name
            {oldDomainsSortField === 'name' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'clean_name', render: () => (
        <TableCell key="clean_name" sx={{ minWidth: 200, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('clean_name')}>
            Clean Name
            {oldDomainsSortField === 'clean_name' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'note', render: () => (
        <TableCell key="note" sx={{ minWidth: 200, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('note')}>
            Notes
            {oldDomainsSortField === 'note' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'description', render: () => (
        <TableCell key="description" sx={{ minWidth: 400, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>
            Description
            {oldDomainsSortField === 'description' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'unsubscribe', render: () => (
        <TableCell key="unsubscribe" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unsubscribe')}>
            <Tooltip title="Unsubscribe" arrow>
              <span>Un</span>
            </Tooltip>
            {oldDomainsSortField === 'unsubscribe' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'in_mailer', render: () => (
        <TableCell key="in_mailer" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('in_mailer')}>
            <Tooltip title="Inmailer" arrow>
              <span>In</span>
            </Tooltip>
            {oldDomainsSortField === 'in_mailer' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'state_name', render: () => (
        <TableCell key="state_name" sx={{ minWidth: 80, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state_name')}>
            State
            {oldDomainsSortField === 'state_name' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'location', render: () => (
        <TableCell key="location" sx={{ minWidth: 150, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('location')}>
            Location
            {oldDomainsSortField === 'location' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'country_name', render: () => (
        <TableCell key="country_name" sx={{ minWidth: 100, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('country_name')}>
            Country
            {oldDomainsSortField === 'country_name' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'company_address', render: () => (
        <TableCell key="company_address" sx={{ minWidth: 150, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('company_address')}>
            Company Address
            {oldDomainsSortField === 'company_address' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'snov_id', render: () => (
        <TableCell key="snov_id" sx={{ minWidth: 100, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('snov_id')}>
            Snov ID
            {oldDomainsSortField === 'snov_id' && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Box>
        </TableCell>
      )},
      { id: 'industry_tag_name', render: () => (
        <TableCell key="industry_tag_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag</TableCell>
      )},
      { id: 'industry_tag_two_name', render: () => (
        <TableCell key="industry_tag_two_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Two</TableCell>
      )},
      { id: 'industry_tag_three_name', render: () => (
        <TableCell key="industry_tag_three_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Three</TableCell>
      )},
      { id: 'industry_tag_four_name', render: () => (
        <TableCell key="industry_tag_four_name" sx={{ minWidth: 130, fontWeight: 'bold' }}>Industry Tag Four</TableCell>
      )},
      { id: 'deliverable', render: () => (
        <TableCell key="deliverable" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Deliverable" arrow>
            <span>Del</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'risky', render: () => (
        <TableCell key="risky" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Risky" arrow>
            <span>Ris</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'undeliverable', render: () => (
        <TableCell key="undeliverable" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Undeliverable" arrow>
            <span>Und</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'bounce', render: () => (
        <TableCell key="bounce" align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
          <Tooltip title="Bounce" arrow>
            <span>Bou</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'Linkedin', render: () => (
        <TableCell key="Linkedin" sx={{ minWidth: 60, fontWeight: 'bold' }}>LI</TableCell>
      )},
      { id: 'employees_onlinkedin', render: () => (
        <TableCell key="employees_onlinkedin" sx={{ minWidth: 140, fontWeight: 'bold' }}>Employees on LinkedIn</TableCell>
      )},
      { id: 'linkedin_id', render: () => (
        <TableCell key="linkedin_id" sx={{ minWidth: 100, fontWeight: 'bold' }}>LinkedIn ID</TableCell>
      )},
      { id: 'twitter', render: () => (
        <TableCell key="twitter" sx={{ minWidth: 60, fontWeight: 'bold' }}>TW</TableCell>
      )},
      { id: 'Facebook', render: () => (
        <TableCell key="Facebook" sx={{ minWidth: 60, fontWeight: 'bold' }}>FB</TableCell>
      )},
      { id: 'ig', render: () => (
        <TableCell key="ig" sx={{ minWidth: 60, fontWeight: 'bold' }}>IG</TableCell>
      )},
      { id: 'Youtube', render: () => (
        <TableCell key="Youtube" sx={{ minWidth: 60, fontWeight: 'bold' }}>YT</TableCell>
      )},
      { id: 'license', render: () => (
        <TableCell key="license" sx={{ minWidth: 100, fontWeight: 'bold' }}>License</TableCell>
      )},
      { id: 'phone', render: () => (
        <TableCell key="phone" sx={{ minWidth: 100, fontWeight: 'bold' }}>Phone</TableCell>
      )},
      { id: 'unknown', render: () => (
        <TableCell key="unknown" align="right" sx={{ minWidth: 80, fontWeight: 'bold' }}>Unknown</TableCell>
      )},
      { id: 'not_verified', render: () => (
        <TableCell key="not_verified" align="right" sx={{ minWidth: 100, fontWeight: 'bold' }}>Not Verified</TableCell>
      )},
      { id: 'suspicious', render: () => (
        <TableCell key="suspicious" sx={{ minWidth: 100, fontWeight: 'bold' }}>Suspicious</TableCell>
      )},
      { id: 'type_', render: () => (
        <TableCell key="type_" sx={{ minWidth: 100, fontWeight: 'bold' }}>Type</TableCell>
      )},
      { id: 'status', render: () => (
        <TableCell key="status" sx={{ minWidth: 100, fontWeight: 'bold' }}>Status</TableCell>
      )},
    ];

    return headerCells.filter(c => isColumnVisible(c.id)).map(c => c.render());
  };

  // Render old domain table row with conditional columns
  const renderOldDomainRow = (domain: any) => {
    const cells = [
      { id: 'checkbox', render: () => (
        <TableCell key="checkbox">
          <Checkbox
            checked={selectedOldDomains.has(domain.id)}
            onChange={() => handleSelectOldDomain(domain.id)}
            size="small"
          />
        </TableCell>
      )},
      { id: 'website', render: () => (
        <TableCell key="website" sx={{ fontWeight: 'medium' }}>
          {renderCompactLink(domain.website || domain.domain, 'website')}
        </TableCell>
      )},
      { id: 'name', render: () => (
        <TableCell key="name">{domain.name || domain.company_name || '-'}</TableCell>
      )},
      { id: 'clean_name', render: () => (
        <TableCell key="clean_name">{domain.clean_name || '-'}</TableCell>
      )},
      { id: 'note', render: () => (
        <TableCell key="note">
          <Tooltip title={String(domain.note || '')} arrow>
            <span>{truncateText(String(domain.note || ''), 30)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'description', render: () => (
        <TableCell key="description">
          <Tooltip title={String(domain.description || domain.company_description || '')} arrow>
            <span>{truncateText(String(domain.description || domain.company_description || ''), 80)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'unsubscribe', render: () => (
        <TableCell key="unsubscribe" align="right">{domain.unsubscribe ?? '-'}</TableCell>
      )},
      { id: 'in_mailer', render: () => (
        <TableCell key="in_mailer" align="right">{domain.in_mailer ?? '-'}</TableCell>
      )},
      { id: 'state_name', render: () => (
        <TableCell key="state_name">{domain.state_name || '-'}</TableCell>
      )},
      { id: 'location', render: () => (
        <TableCell key="location">{domain.location || '-'}</TableCell>
      )},
      { id: 'country_name', render: () => (
        <TableCell key="country_name">{domain.country_name || '-'}</TableCell>
      )},
      { id: 'company_address', render: () => (
        <TableCell key="company_address">
          <Tooltip title={String(domain.company_address || '')} arrow>
            <span>{truncateText(String(domain.company_address || ''), 30)}</span>
          </Tooltip>
        </TableCell>
      )},
      { id: 'snov_id', render: () => (
        <TableCell key="snov_id">{domain.snov_id || '-'}</TableCell>
      )},
      { id: 'industry_tag_name', render: () => (
        <TableCell key="industry_tag_name">{domain.industry_tag_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_two_name', render: () => (
        <TableCell key="industry_tag_two_name">{domain.industry_tag_two_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_three_name', render: () => (
        <TableCell key="industry_tag_three_name">{domain.industry_tag_three_name || '-'}</TableCell>
      )},
      { id: 'industry_tag_four_name', render: () => (
        <TableCell key="industry_tag_four_name">{domain.industry_tag_four_name || '-'}</TableCell>
      )},
      { id: 'deliverable', render: () => (
        <TableCell key="deliverable" align="right">{domain.deliverable ?? '-'}</TableCell>
      )},
      { id: 'risky', render: () => (
        <TableCell key="risky" align="right">{domain.risky ?? '-'}</TableCell>
      )},
      { id: 'undeliverable', render: () => (
        <TableCell key="undeliverable" align="right">{domain.undeliverable ?? '-'}</TableCell>
      )},
      { id: 'bounce', render: () => (
        <TableCell key="bounce" align="right">{domain.bounce ?? '-'}</TableCell>
      )},
      { id: 'Linkedin', render: () => (
        <TableCell key="Linkedin">{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
      )},
      { id: 'employees_onlinkedin', render: () => (
        <TableCell key="employees_onlinkedin">{domain.employees_onlinkedin || '-'}</TableCell>
      )},
      { id: 'linkedin_id', render: () => (
        <TableCell key="linkedin_id">{domain.linkedin_id || '-'}</TableCell>
      )},
      { id: 'twitter', render: () => (
        <TableCell key="twitter">{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
      )},
      { id: 'Facebook', render: () => (
        <TableCell key="Facebook">{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
      )},
      { id: 'ig', render: () => (
        <TableCell key="ig">{renderCompactLink(domain.ig, 'ig')}</TableCell>
      )},
      { id: 'Youtube', render: () => (
        <TableCell key="Youtube">{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
      )},
      { id: 'license', render: () => (
        <TableCell key="license">{domain.license || '-'}</TableCell>
      )},
      { id: 'phone', render: () => (
        <TableCell key="phone">{domain.phone || '-'}</TableCell>
      )},
      { id: 'unknown', render: () => (
        <TableCell key="unknown" align="right">{domain.unknown ?? '-'}</TableCell>
      )},
      { id: 'not_verified', render: () => (
        <TableCell key="not_verified" align="right">{domain.not_verified ?? '-'}</TableCell>
      )},
      { id: 'suspicious', render: () => (
        <TableCell key="suspicious">{domain.suspicious || '-'}</TableCell>
      )},
      { id: 'type_', render: () => (
        <TableCell key="type_">{domain.type_ || '-'}</TableCell>
      )},
      { id: 'status', render: () => (
        <TableCell key="status">{domain.status || '-'}</TableCell>
      )},
    ];

    return cells.filter(c => isColumnVisible(c.id)).map(c => c.render());
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/outreach')} sx={{ mt: 2 }}>
          Back to Upload
        </Button>
      </Box>
    );
  }

  // Function to truncate text
  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden',
      p: 3
    }}>

      {/* New Domains Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            New domains: {newDomains.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              size="small"
              onClick={() => setShowBackDialog(true)}
              disabled={isDeleting}
            >
              BACK
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowSaveDialog(true)}
              disabled={isLoading || isSaving}
            >
              SAVE SESSION
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleContinue}
              disabled={isLoading || isSaving}
            >
              CONTINUE
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* New Domains Table */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
          maxHeight: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          <Paper sx={{ width: '100%', overflow: 'visible', boxShadow: 'none' }}>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader sx={{ minWidth: 2000, '& .MuiTableCell-root': { padding: '4px 4px' } }}>
                <TableHead>
                  <TableRow>
                    {/* New domains: Industry Tag and Type first, then company columns in order */}
                    {renderNewDomainHeaderCells()}
                  </TableRow>
                </TableHead>
                {/* OLD NEW DOMAINS HEADER CODE REMOVED - replaced with renderNewDomainHeaderCells() */}
                {false && (
                  <TableHead>
                    <TableRow>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Industry Tag</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Website</TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Company Name</TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Clean Name</TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Notes</TableCell>
                    <TableCell sx={{ minWidth: 400, fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
                      <Tooltip title="Unsubscribe" arrow>
                        <span>Un</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
                      <Tooltip title="Inmailer" arrow>
                        <span>In</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>State</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>Country</TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Company Address</TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>Snov ID</TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Deliverable" arrow>
                        <span>Del</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Risky" arrow>
                        <span>Ris</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Undeliverable" arrow>
                        <span>Und</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Bounce" arrow>
                        <span>Bou</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>LI</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Employees on LinkedIn</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>LinkedIn ID</TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>TW</TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>FB</TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>IG</TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>YT</TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>License</TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell align="right" sx={{ minWidth: 100, fontWeight: 'bold' }}>Unknown</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold' }}>Not Verified</TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>Suspicious</TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                )}
                <TableBody>
                  {newDomains
                    .slice(0, newDomainsDisplayCount)
                    .map((domain) => {
                      // Get tags in order: industry_tag, industry_tag_two, industry_tag_three, industry_tag_four
                      const selectedTags: { id: number; name: string }[] = [];
                      const safeIndustryTags = Array.isArray(industryTags) ? industryTags : [];
                      if (domain.industry_tag) {
                        const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag));
                        if (tag) selectedTags.push(tag);
                      }
                      if (domain.industry_tag_two) {
                        const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_two));
                        if (tag) selectedTags.push(tag);
                      }
                      if (domain.industry_tag_three) {
                        const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_three));
                        if (tag) selectedTags.push(tag);
                      }
                      if (domain.industry_tag_four) {
                        const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_four));
                        if (tag) selectedTags.push(tag);
                      }
                      const isMaxSelected = selectedTags.length >= 4;
                      const isOpen = openDropdowns.get(domain.id) || false;
                      
                      return (
                        <TableRow key={domain.id} hover>
                          <TableCell>
                            <Autocomplete
                              multiple
                              options={safeIndustryTags}
                              value={selectedTags}
                              open={isMaxSelected ? false : isOpen}
                              onOpen={() => {
                                if (!isMaxSelected) {
                                  setOpenDropdowns(prev => new Map(prev).set(domain.id, true));
                                }
                              }}
                              onClose={() => {
                                setOpenDropdowns(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(domain.id, false);
                                  return newMap;
                                });
                              }}
                              onChange={(event, newValue) => {
                                const limitedValue = newValue.slice(0, 4);
                                handleStatusChange(domain.id, limitedValue);
                                if (limitedValue.length >= 4) {
                                  setOpenDropdowns(prev => {
                                    const newMap = new Map(prev);
                                    newMap.set(domain.id, false);
                                    return newMap;
                                  });
                                }
                              }}
                              getOptionLabel={(option) => option?.name ?? ''}
                              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                              loading={safeIndustryTags.length === 0}
                              size="small"
                              limitTags={2}
                              sx={{ 
                                minWidth: 200,
                                '& .MuiInputBase-root': {
                                  fontSize: '0.75rem',
                                  minHeight: '30px',
                                  padding: '0 !important',
                                },
                                '& .MuiInputBase-input': {
                                  padding: '4px 8px !important',
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={params.inputProps.value ? '' : '-'}
                                  variant="outlined"
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: isMaxSelected,
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.75rem',
                                      minHeight: '30px',
                                    },
                                  }}
                                />
                              )}
                              renderTags={(value, getTagProps) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                      <Chip
                                        key={key}
                                        {...tagProps}
                                        label={option.name}
                                        size="small"
                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                      />
                                    );
                                  })}
                                  {isMaxSelected && (
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', alignSelf: 'center', ml: 0.5 }}>
                                      (max)
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              clearOnEscape
                              selectOnFocus
                              handleHomeEndKeys
                              noOptionsText={industryTags.length === 0 ? 'Loading tags...' : 'No matches'}
                            />
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              value={domain.type_ || null}
                              onChange={(event, newValue) => {
                                handleTypeChange(domain.id, newValue || '');
                              }}
                              options={TYPE_OPTIONS}
                              size="small"
                              sx={{ 
                                minWidth: 150,
                                '& .MuiInputBase-root': {
                                  fontSize: '0.75rem',
                                  minHeight: '30px',
                                  padding: '0 !important',
                                },
                                '& .MuiInputBase-input': {
                                  padding: '4px 8px !important',
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="-"
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.75rem',
                                      minHeight: '30px',
                                    },
                                  }}
                                />
                              )}
                              clearOnEscape
                              selectOnFocus
                              handleHomeEndKeys
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'medium' }}>{renderCompactLink(domain.website || domain.domain, 'website')}</TableCell>
                          <TableCell>{domain.name || domain.company_name || '-'}</TableCell>
                          <TableCell>{domain.clean_name || '-'}</TableCell>
                          <TableCell>
                            <Tooltip title={String(domain.note || '')} arrow>
                              <span>{truncateText(String(domain.note || ''), 30)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={String(domain.description || domain.company_description || '')} arrow>
                              <span>{truncateText(String(domain.description || domain.company_description || ''), 80)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{domain.unsubscribe ?? '-'}</TableCell>
                          <TableCell align="right">{domain.in_mailer ?? '-'}</TableCell>
                          <TableCell>{domain.state_name || '-'}</TableCell>
                          <TableCell>{domain.location || '-'}</TableCell>
                          <TableCell>{domain.country_name || '-'}</TableCell>
                          <TableCell>
                            <Tooltip title={String(domain.company_address || '')} arrow>
                              <span>{truncateText(String(domain.company_address || ''), 30)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{domain.snov_id || '-'}</TableCell>
                          <TableCell align="right">{domain.deliverable ?? '-'}</TableCell>
                          <TableCell align="right">{domain.risky ?? '-'}</TableCell>
                          <TableCell align="right">{domain.undeliverable ?? '-'}</TableCell>
                          <TableCell align="right">{domain.bounce ?? '-'}</TableCell>
                          <TableCell>{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
                          <TableCell>{domain.employees_onlinkedin || '-'}</TableCell>
                          <TableCell>{domain.linkedin_id || '-'}</TableCell>
                          <TableCell>{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
                          <TableCell>{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
                          <TableCell>{renderCompactLink(domain.ig, 'ig')}</TableCell>
                          <TableCell>{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
                          <TableCell>{domain.license || '-'}</TableCell>
                          <TableCell>{domain.phone || '-'}</TableCell>
                          <TableCell align="right">{domain.unknown ?? '-'}</TableCell>
                          <TableCell align="right">{domain.not_verified ?? '-'}</TableCell>
                          <TableCell>{domain.suspicious || '-'}</TableCell>
                          <TableCell>{domain.status || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
      
      {/* Load More Section - New Domains */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {Math.min(newDomainsDisplayCount, newDomains.length)} of {newDomains.length}
        </Typography>
        
        {isLoadingMoreNew ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : newDomainsDisplayCount < newDomains.length ? (
          <button 
            onClick={handleLoadMoreNew}
            style={{
              padding: '6px 16px',
              backgroundColor: 'transparent',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
              (e.target as HTMLButtonElement).style.color = 'white';
            }}
            onMouseOut={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.target as HTMLButtonElement).style.color = '#1976d2';
            }}
          >
            Load More
          </button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        )}
      </Box>

      {/* Old Domains Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 2
      }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          Old domains: {filteredOldDomains.length} {appliedFilters.length > 0 && `(filtered from ${oldDomains.length})`}
        </Typography>
        
        {/* Filter Panel */}
        <Box sx={{ mb: 2 }}>
          <CompactFilterPanel
            columns={visibleColumns}
            onColumnToggle={handleColumnToggle}
            onFiltersApply={handleFiltersApply}
            onClearFilters={handleClearFilters}
            onAddNew={() => {}}
            filterType="company"
            showFilters={true}
          />
        </Box>
      </Box>
      
      {/* Old Domains Table */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
          maxHeight: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          <Paper sx={{ width: '100%', overflow: 'visible', boxShadow: 'none' }}>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader sx={{ minWidth: 2500, '& .MuiTableCell-root': { padding: '4px 4px' } }}>
                <TableHead>
                  <TableRow>
                    {/* Old domains: Checkbox first, then all company columns in order */}
                    {renderOldDomainHeaderCells()}
                  </TableRow>
                </TableHead>
                {/* OLD HEADER CODE REMOVED - replaced with renderOldDomainHeaderCells() */}
                {false && (
                  <TableHead>
                    <TableRow>
                    <TableCell sx={{ minWidth: 50, fontWeight: 'bold' }}>
                      <Checkbox
                        checked={selectAllOld}
                        onChange={handleSelectAllOld}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('website')}>
                        Website
                        {oldDomainsSortField === 'website' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                        Company Name
                        {oldDomainsSortField === 'name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('clean_name')}>
                        Clean Name
                        {oldDomainsSortField === 'clean_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('note')}>
                        Notes
                        {oldDomainsSortField === 'note' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 400, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>
                        Description
                        {oldDomainsSortField === 'description' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unsubscribe')}>
                        <Tooltip title="Unsubscribe" arrow>
                          <span>Un</span>
                        </Tooltip>
                        {oldDomainsSortField === 'unsubscribe' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 45, fontWeight: 'bold', fontSize: '12px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('in_mailer')}>
                        <Tooltip title="Inmailer" arrow>
                          <span>In</span>
                        </Tooltip>
                        {oldDomainsSortField === 'in_mailer' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state_name')}>
                        State
                        {oldDomainsSortField === 'state_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('location')}>
                        Location
                        {oldDomainsSortField === 'location' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('country_name')}>
                        Country
                        {oldDomainsSortField === 'country_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('company_address')}>
                        Company Address
                        {oldDomainsSortField === 'company_address' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('snov_id')}>
                        Snov ID
                        {oldDomainsSortField === 'snov_id' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_name')}>
                        Industry Tag
                        {oldDomainsSortField === 'industry_tag_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_two_name')}>
                        Industry Tag Two
                        {oldDomainsSortField === 'industry_tag_two_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_three_name')}>
                        Industry Tag Three
                        {oldDomainsSortField === 'industry_tag_three_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('industry_tag_four_name')}>
                        Industry Tag Four
                        {oldDomainsSortField === 'industry_tag_four_name' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Deliverable" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('deliverable')}>
                          Del
                          {oldDomainsSortField === 'deliverable' && (
                            <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                              {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Risky" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('risky')}>
                          Ris
                          {oldDomainsSortField === 'risky' && (
                            <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                              {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Undeliverable" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('undeliverable')}>
                          Und
                          {oldDomainsSortField === 'undeliverable' && (
                            <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                              {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold' }}>
                      <Tooltip title="Bounce" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('bounce')}>
                          Bou
                          {oldDomainsSortField === 'bounce' && (
                            <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                              {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Linkedin')}>
                        LinkedIn
                        {oldDomainsSortField === 'Linkedin' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('employees_onlinkedin')}>
                        Employees on LinkedIn
                        {oldDomainsSortField === 'employees_onlinkedin' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('linkedin_id')}>
                        LinkedIn ID
                        {oldDomainsSortField === 'linkedin_id' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('twitter')}>
                        TW
                        {oldDomainsSortField === 'twitter' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Facebook')}>
                        FB
                        {oldDomainsSortField === 'Facebook' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ig')}>
                        IG
                        {oldDomainsSortField === 'ig' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Youtube')}>
                        YT
                        {oldDomainsSortField === 'Youtube' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('license')}>
                        License
                        {oldDomainsSortField === 'license' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('phone')}>
                        Phone
                        {oldDomainsSortField === 'phone' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unknown')}>
                        Unknown
                        {oldDomainsSortField === 'unknown' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('not_verified')}>
                        Not Verified
                        {oldDomainsSortField === 'not_verified' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('suspicious')}>
                        Suspicious
                        {oldDomainsSortField === 'suspicious' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('type_')}>
                        Type
                        {oldDomainsSortField === 'type_' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                        Status
                        {oldDomainsSortField === 'status' && (
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            {oldDomainsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                )}
                <TableBody>
                  {sortedOldDomains
                    .slice(0, oldDomainsDisplayCount)
                    .map((domain: any) => (
                      <TableRow key={domain.id} hover role="checkbox" tabIndex={-1}>
                        {renderOldDomainRow(domain)}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
      
      {/* Load More Section - Old Domains */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {Math.min(oldDomainsDisplayCount, filteredOldDomains.length)} of {filteredOldDomains.length}
        </Typography>
        
        {isLoadingMoreOld ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : oldDomainsDisplayCount < filteredOldDomains.length ? (
          <button 
            onClick={handleLoadMoreOld}
            style={{
              padding: '6px 16px',
              backgroundColor: 'transparent',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
              (e.target as HTMLButtonElement).style.color = 'white';
            }}
            onMouseOut={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.target as HTMLButtonElement).style.color = '#1976d2';
            }}
          >
            Load More
          </button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        )}
      </Box>

      {/* Confirmation Dialog for Back Button */}
      <Dialog
        open={showBackDialog}
        onClose={() => !isDeleting && setShowBackDialog(false)}
        aria-labelledby="back-dialog-title"
        aria-describedby="back-dialog-description"
      >
        <DialogTitle id="back-dialog-title">
          Confirm Exit
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="back-dialog-description">
            Are you sure you want to go back? If you proceed, all progress for this session will be lost. 
            The data will be truncated and you will need to upload again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowBackDialog(false)} 
            disabled={isDeleting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (!sessionId) {
                setShowBackDialog(false);
                navigate('/outreach');
                return;
              }

              try {
                setIsDeleting(true);
                await ApiService.deleteSession(sessionId);
                setShowBackDialog(false);
                navigate('/outreach');
              } catch (error: any) {
                console.error('Error deleting session:', error);
                alert(`Failed to delete session: ${error.message || 'Unknown error'}`);
              } finally {
                setIsDeleting(false);
              }
            }}
            disabled={isDeleting}
            color="error"
            variant="contained"
          >
            {isDeleting ? 'Deleting...' : 'Go Back & Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Session Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => !isSaving && setShowSaveDialog(false)}
      >
        <DialogTitle>Save Session</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give this session a name so you can resume it later from the Outreach Hub.
            This will save all your current domain selections and labels.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSession} 
            variant="contained" 
            disabled={isSaving || !sessionName.trim()}
          >
            {isSaving ? 'Saving...' : 'Save & Exit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      {saveSuccess && (
        <Dialog open={!!saveSuccess} onClose={() => setSaveSuccess(null)}>
          <DialogTitle>Success</DialogTitle>
          <DialogContent>
            <Alert severity="success">{saveSuccess}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveSuccess(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default OutreachProcessPage;
